import { Component, inject, OnInit, ChangeDetectionStrategy } from "@angular/core";
import { SegmentService } from "@front/m19-services";
import {
  IButtonDirective,
  IFormFieldComponent,
  IInputDirective,
  ILabelComponent,
  ITooltipDirective,
  MODAL_DATA,
  ModalOptions,
  ModalRef,
  ToasterService,
} from "@front/m19-ui";
import { of, switchMap, take } from "rxjs";
import { SegmentEx } from "@front/m19-models";
import { Marketplace, MatchType, Targeting } from "@front/m19-api-client";
import { TranslocoDirective, TranslocoService } from "@jsverse/transloco";
import { FormsModule } from "@angular/forms";
import { AsinSelectionComponent } from "@m19-board/shared/asin-selection/asin-selection.component";

export interface ProductSegmentModalData {
  segment?: SegmentEx;
  marketplace: Marketplace;
  accountId: string;
  initSegmentItems?: Targeting[];
  isReadOnly?: boolean;
}

@Component({
  selector: "app-product-segment-modal",
  templateUrl: "./product-segment-modal.component.html",
  imports: [
    TranslocoDirective,
    FormsModule,
    IButtonDirective,
    ITooltipDirective,
    AsinSelectionComponent,
    IInputDirective,
    IFormFieldComponent,
    ILabelComponent,
  ],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class ProductSegmentModalComponent implements OnInit {
  static readonly defaultModalOptions: Omit<ModalOptions<unknown>, "data"> = { size: "lg", height: "fill" };

  private segmentService = inject(SegmentService);
  private toastrService = inject(ToasterService);
  private translocoService = inject(TranslocoService);

  modalData = inject(MODAL_DATA) as ProductSegmentModalData;
  modalRef = inject(ModalRef);

  accountId: string = this.modalData.accountId;
  marketplace: Marketplace = this.modalData.marketplace;
  initSegmentItems: Targeting[] = this.modalData.initSegmentItems || [];
  isReadOnly = this.modalData.isReadOnly || false;

  existingSegment!: SegmentEx;
  name: string = "";
  asinsList: string[] = [];

  ngOnInit(): void {
    if (this.initSegmentItems.length) {
      this.asinsList = this.initSegmentItems.map((i) => i.targetingValue);
    }
    if (this.modalData.segment) {
      this.existingSegment = this.modalData.segment;
      this.name = this.existingSegment.name;
      this.asinsList = this.existingSegment.items
        .filter((a) => a.matchType == MatchType.asinSameAs)
        .map((a) => a.targetingValue);
    }
  }

  saveSegment() {
    if (this.existingSegment) {
      this.updateSegment();
    } else {
      this.createSegment();
    }
  }

  close() {
    this.modalRef.close();
  }

  private updateSegment() {
    (this.name !== this.existingSegment.name
      ? this.segmentService.updateSegmentName(
          this.accountId,
          this.marketplace,
          this.existingSegment.segmentId,
          this.name,
        )
      : of(this.existingSegment)
    )
      .pipe(
        switchMap((segment) => {
          const asinsToDelete = [];
          for (const asin of this.existingSegment.getProductTargetingItems()) {
            if (this.asinsList.findIndex((s) => s == asin.targetingValue) < 0) {
              asinsToDelete.push(asin);
            }
          }
          if (asinsToDelete.length == 0) {
            return of(segment);
          }
          return this.segmentService.deleteItemsFromSegment(
            this.accountId,
            this.marketplace,
            this.existingSegment.segmentId,
            asinsToDelete,
          );
        }),
        switchMap((segment) => {
          const asinsToAdd = [];
          for (const asin of this.asinsList) {
            if (this.existingSegment.getProductTargetingItems().findIndex((s) => s.targetingValue == asin) < 0) {
              asinsToAdd.push(asin);
            }
          }
          if (asinsToAdd.length == 0) {
            return of(segment);
          }
          return this.segmentService.addAsinsToSegment(
            this.accountId,
            this.marketplace,
            this.existingSegment.segmentId,
            asinsToAdd,
          );
        }),
        take(1),
      )
      .subscribe({
        next: (s) => {
          this.toastrService.success(
            this.translocoService.translate("keyword-segment-modal.segment_s_name_updated", [s.name]),
            this.translocoService.translate("keyword-segment-modal.segment_updated"),
          );
          this.modalRef.close();
        },
        error: (err) => {
          this.toastrService.error(
            this.translocoService.translate("keyword-segment-modal.error_updating_segment") + ": " + err,
            this.translocoService.translate("keyword-segment-modal.segment_update_error"),
          );
        },
      });
  }

  private createSegment() {
    this.segmentService
      .createSegment(this.accountId, this.marketplace, this.name)
      .pipe(
        switchMap((segment: SegmentEx) => {
          return this.segmentService.addAsinsToSegment(
            this.accountId,
            this.marketplace,
            segment.segmentId,
            this.asinsList,
          );
        }),
        take(1),
      )
      .subscribe({
        next: (s) => {
          this.toastrService.success(
            this.translocoService.translate("keyword-segment-modal.segment_s_name_created", [s.name]),
            this.translocoService.translate("keyword-segment-modal.segment_created"),
          );
          this.modalRef.close(s);
        },
        error: (err) => {
          this.toastrService.error(
            this.translocoService.translate("keyword-segment-modal.error_creating_segment") + ": " + err,
            this.translocoService.translate("keyword-segment-modal.segment_creation_error"),
          );
        },
      });
  }

  isInvalid() {
    return !this.name || this.asinsList.length == 0;
  }

  invalidTooltip() {
    if (!this.name) {
      return this.translocoService.translate("product-segment-modal.enter_a_name_for_this_product_segment");
    }
    if (this.asinsList.length == 0) {
      return this.translocoService.translate("product-segment-modal.set_asins_for_this_product_segment");
    }
    return "";
  }
}
